import { lazy, Suspense, useEffect, useState } from "react";
import { QueryClient, QueryClientProvider, useQuery } from "react-query";
import BackgroundDataLoader from "@components/BackgroundDataLoader";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useMutation } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { CookieConsentBanner } from "@components/CookieConsent";
import jwt_decode from "jwt-decode";
import Spinner from "@components/Spinner/Spinner";
import useWindowDimensions from "@hooks/useWindowDimension";
import { mobileView, desktopView } from "./redux/viewSlice";
import Dashboard from "@modules/Dashboard";
import Header from "@components/Header/Header";
import PublicHeader from "@components/Header/PublicHeader";
import {
  PublicProjectDetailMapView,
  ProjectDetailMapView,
  ProjectDetailListView,
  ProjectDetailDashboardView,
} from "@modules/Projects/pages/ProjectDetail";
import WithoutProjectView from "@modules/Projects/pages/WithoutProjectView";
import WithoutProjectMapView from "@modules/Projects/pages/WithoutProjectView/MapView";
import WithoutProjectDashboardView from "@modules/Projects/pages/WithoutProjectView/DashboardView";
import { ProjectGridView, ProjectMapView } from "@modules/Projects/components";
import IrrigationAreaPage from "@modules/IrrigationArea/components";
import ProfilePage from "@modules/Profile/component/ProfilePage";
import SensorPage from "@modules/Sensors/components";
import UserPage from "@modules/Users/components";
import TreePage, { TreeProfileDetail } from "@modules/Trees/components";
import DataScience from "@modules/DataScience";
import StandaloneDataScience from "@modules/DataScience/pages/StandaloneDataScience";

const noop = () => undefined;
import StandaloneMapView from "@modules/Projects/pages/StandaloneMapView";
import ExternalDisplay from "@modules/ExternalDisplay";
import ExternalDisplayV2 from "@modules/ExternalDisplay/ExternalDisplayV2";
import ExternalDisplayV3 from "@modules/ExternalDisplay/ExternalDisplayV3";
import Billing from "@modules/Payment/components/Billing";
import PaymentSuccess from "@modules/Payment/components/PaymentSuccess";
import { useLocation } from "react-router-dom";

import { actions } from "@modules/Auth/_redux/authRedux";
import { getCurrentUserRBAC, refresh } from "@modules/Auth/_redux/authCrud";
import { ProjectFilterProvider } from "./context/ProjectFilterContext";
import { getPersonaHomePath, hasPermission } from "@helper/permissions";
import TourWrapper from "@modules/Tour/TourWrapper";

const Login = lazy(() => import("@modules/Auth/components/Login"));

const Signup = lazy(() => import("@modules/Auth/components/Signup"));
const CreateCustomer = lazy(() => import("@modules/Auth/components/CreateCustomer"));
const SignupSuccess = lazy(() => import("@modules/Auth/components/SignupSuccess"));
const ForgotPassword = lazy(() => import("@modules/Auth/components/ForgotPassword"));
const ResetPassword = lazy(() => import("@modules/Auth/components/ResetPassword"));
const ActivateAccount = lazy(() => import("@modules/Auth/components/ActivateAccount"));
const SettingPage = lazy(() => import("@modules/Settings/components"));
const CustomerPage = lazy(() => import("@modules/Customer/components"));
const AdminCustomerPage = lazy(() => import("@modules/Admin/components"));
const TreeDetail = lazy(() => import("@modules/Trees/components/TreeDetail"));
const SensorDetail = lazy(() => import("@modules/Sensors/components/SensorDetail"));
const TaskPage = lazy(() => import("@modules/Tasks/components/TaskPage"));
const PageNotFound = lazy(() => import("@components/Error/PageNotFound"));
const FeedbackPage = lazy(() => import("@modules/Support/components/FeedbackPage"));
const BetaEnded = lazy(() => import("@components/Error/BetaEnded"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data is considered fresh for 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes - keep unused data in cache for 10 minutes
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      refetchOnMount: false, // Don't refetch on component mount if data exists
      retry: 1, // Only retry failed requests once
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff

      // Concurrent request optimizations
      refetchOnReconnect: "always", // Refetch when network reconnects
      suspense: false, // Disable suspense to prevent blocking
      useErrorBoundary: false, // Handle errors gracefully

      // Network mode optimizations
      networkMode: "online", // Only run queries when online

      // Query scheduling
      queryKeyHashFn: (queryKey) => JSON.stringify(queryKey), // Efficient cache keys
    },
    mutations: {
      retry: 1, // Only retry failed mutations once
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Mutation optimizations
      networkMode: "online",
      useErrorBoundary: false,
    },
  },
});

const PublicRoute = ({ children }) => {
  return (
    <div id="ts_wrapper">
      <PublicHeader />
      <main className="page-container">{children}</main>
    </div>
  );
};

const PaidOnlyRoute = ({ children }) => {
  const { auth } = useSelector((state) => state);

  // Check if user is authenticated
  if (!auth?.authToken || !auth?.user) {
    return <Navigate to="/login" replace />;
  }

  // Only allow access if customer.paid is true
  if (!auth.user.paid) {
    console.log("[PaidOnlyRoute] User is not paid, redirecting to sensors");
    return <Navigate to="/sensors" replace />;
  }

  return children;
};

const UnpaidOnlyRoute = ({ children }) => {
  const { auth } = useSelector((state) => state);

  // Check if user is authenticated
  if (!auth?.authToken || !auth?.user) {
    return <Navigate to="/login" replace />;
  }

  // Only allow access if customer.paid is false (unpaid users)
  if (auth.user.paid) {
    console.log("[UnpaidOnlyRoute] User is paid, redirecting to projects");
    return <Navigate to="/projects" replace />;
  }

  return children;
};

const PermissionRoute = ({ permission, children }) => {
  const { auth } = useSelector((state) => state);
  if (!hasPermission(auth?.user, permission)) {
    return <Navigate to={getPersonaHomePath(auth?.user)} replace />;
  }
  return children;
};

const SettingsRoute = ({ children }) => {
  const { auth } = useSelector((state) => state);
  const user = auth?.user;
  const { section } = useParams();
  const isBasicUser = !hasPermission(user, "view_users");

  if (hasPermission(user, "view_settings")) {
    return children;
  }

  if (isBasicUser && (!section || section === "profile")) {
    return children;
  }

  return <Navigate to={getPersonaHomePath(user)} replace />;
};

const SettingsIndexRoute = () => {
  const { auth } = useSelector((state) => state);
  const user = auth?.user;
  const isBasicUser = !hasPermission(user, "view_users");

  return <Navigate to={isBasicUser ? "/settings/profile" : "/settings/appearance"} replace />;
};

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const { auth } = useSelector((state) => state);
  const dispatch = useDispatch();
  const { isLoading: loadingRefresh, mutateAsync: refreshMutate } = useMutation(refresh, {
    onSuccess: async (data) => {
      const accessToken = data.access;
      const refreshExp = data.refreshExp;
      const decoded = jwt_decode(accessToken);
      await dispatch(actions.login(accessToken));
      await dispatch(actions.refreshExp(refreshExp));
      await dispatch(actions.userLoaded(decoded));
      try {
        const me = await getCurrentUserRBAC();
        await dispatch(actions.setUser(me));
      } catch (meError) {
        console.warn("Failed to refresh /api/me after token refresh", meError);
      }
    },
  });

  const { isAuthorized, isExpiredAccess, isExpiredRefresh, refreshExp } = useSelector(
    ({ auth }) => ({
      isAuthorized: auth.authToken != null,
      isExpiredAccess: auth.authToken
        ? new Date(jwt_decode(auth.authToken).exp * 1000).getTime() < new Date().getTime()
        : false,
      isExpiredRefresh: auth.refreshExp
        ? new Date(auth.refreshExp * 1000).getTime() < new Date().getTime()
        : false,
      refreshExp: auth.refreshExp ? auth.refreshExp : false,
    }),
    shallowEqual
  );
  useQuery(["auth", "me"], getCurrentUserRBAC, {
    enabled: isAuthorized && !isExpiredAccess,
    refetchOnWindowFocus: false,
    retry: 1,
    staleTime: 1000 * 60 * 5,
    onSuccess: (me) => {
      dispatch(actions.setUser(me));
    },
  });
  localStorage.setItem("refreshExp", refreshExp);

  if (!isAuthorized || isExpiredRefresh) {
    return <Navigate to="/login" state={{ from: location }} />;
  }

  if (isAuthorized && isExpiredAccess && !isExpiredRefresh) {
    // && auth.refreshToken != null) {
    // TODO: get new accessToken and set accessToken globally by using actions from authReducer
    if (loadingRefresh) {
      localStorage.setItem("loadingRefresh", "loading");
    } else {
      localStorage.setItem("loadingRefresh", "done");
    }
    {
      loadingRefresh ? (
        <Spinner animation="border" size="sm" />
      ) : (
        (async () =>
          await refreshMutate({
            refresh: auth.refreshToken,
          }))()
      );
    }
  }

  return (
    <div id="ts_wrapper">
      <BackgroundDataLoader />
      <Header />
      <main
        className="page-container"
        style={{
          marginTop: "0", // padding handled via page-container
          transition: "margin-left 0.3s ease",
        }}
      >
        {children}
      </main>
    </div>
  );
};

const UnprotectedRoute = ({ children }) => {
  // TODO: check if expired accessToken
  // if accessToken is expired, check whether rereshToken is expired as well
  // if refreshToken is not expired: get new accessToken
  // else: new Login
  const { isAuthorized, isExpiredAccess, isExpiredRefresh, refreshExp, isPaid } = useSelector(
    ({ auth }) => ({
      isAuthorized: auth.authToken != null,
      isExpiredAccess: auth.authToken
        ? new Date(jwt_decode(auth.authToken).exp * 1000).getTime() < new Date().getTime()
        : false,
      refreshExp: auth.refreshExp ? auth.refreshExp : false,
      isPaid: auth.user?.paid ?? false,
    }),
    shallowEqual
  );
  const { state } = useLocation();
  const auth = useSelector((s) => s.auth);
  const dispatch = useDispatch();
  const shouldResolvePersonaHome = isAuthorized && !isExpiredAccess && !auth?.user?.persona;
  const { isLoading: isLoadingCurrentUser } = useQuery(
    ["auth", "me", "unprotected-route"],
    getCurrentUserRBAC,
    {
      enabled: shouldResolvePersonaHome,
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 1000 * 30,
      onSuccess: (me) => {
        dispatch(actions.setUser(me));
      },
    }
  );
  localStorage.setItem("refreshExp", refreshExp);

  if (shouldResolvePersonaHome && isLoadingCurrentUser) {
    return <Spinner height={"80vh"} />;
  }

  if (isAuthorized && !isExpiredAccess) {
    const previousPath = localStorage.getItem("previousPath");
    const previousSearch = localStorage.getItem("previousSearch");
    let { from } = state || { from: { pathname: getPersonaHomePath(auth.user), search: "" } };
    const UNPAID_ONLY_PATHS = ["/sensors"];
    const isPreviousPathValid =
      previousPath && !(isPaid && UNPAID_ONLY_PATHS.includes(previousPath));
    if (!state && isPreviousPathValid) {
      from = { pathname: previousPath, search: previousSearch };
    }
    return <Navigate to={from.pathname + from.search} replace />;
  }

  return children;
};

const App = () => {
  const { i18n } = useTranslation();

  const { auth } = useSelector((state) => state);
  const { width } = useWindowDimensions();
  const dispatch = useDispatch();

  // Check if we are in beta or local environment
  // const isBetaOrLocal =
  //   process.env.REACT_APP_NODE_ENV === "beta" || process.env.REACT_APP_NODE_ENV === "development";

  // If beta or local, show BetaEnded page for all routes
  // if (isBetaOrLocal) {
  //   return (
  //     <QueryClientProvider client={queryClient}>
  //       <Suspense fallback={<Spinner height={"80vh"} />}>
  //         <BetaEnded />
  //       </Suspense>
  //     </QueryClientProvider>
  //   );
  // }
  //language setting after login
  useEffect(() => {
    if (Object.keys(auth).length != 0 && auth.authToken != null && auth.user != null) {
      const { language } = auth.user;
      let lang = "";
      if (language === 0) lang = "de";
      if (language === 1) lang = "en";
      if (language === 2) lang = "it";
      if (language === 3) lang = "fr";
      if (language === 4) lang = "es";
      if (language === 5) lang = "pt";
      i18n.changeLanguage(lang);
    } else {
      i18n.changeLanguage("de");
    }
  }, []);

  //check the width of device to display mobile or desktop view
  useEffect(() => {
    if (width < 768) {
      dispatch(mobileView());
    } else {
      dispatch(desktopView());
    }
  }, [width]);

  const [project, setProject] = useState(null);

  return (
    <QueryClientProvider client={queryClient}>
      <ProjectFilterProvider>
        {/* BackgroundDataLoader moved to ProtectedRoute to only run when authenticated */}
        <TourWrapper>
          <Suspense fallback={<Spinner height={"80vh"} />}>
            <Routes>
              <Route
                path="/login"
                element={
                  <UnprotectedRoute>
                    <Login />
                  </UnprotectedRoute>
                }
              />
              <Route
                path="/signup"
                element={
                  <UnprotectedRoute>
                    <Signup />
                  </UnprotectedRoute>
                }
              />
              <Route
                path="/signup-successful"
                element={
                  <UnprotectedRoute>
                    <SignupSuccess />
                  </UnprotectedRoute>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <UnprotectedRoute>
                    <ForgotPassword />
                  </UnprotectedRoute>
                }
              />
              <Route
                path="/password-reset/:type/:id"
                element={
                  <UnprotectedRoute>
                    <ResetPassword />
                  </UnprotectedRoute>
                }
              />
              <Route
                path="/activate/:uid/:token"
                element={
                  <UnprotectedRoute>
                    <ActivateAccount />
                  </UnprotectedRoute>
                }
              />
              {/* <Route path="/authentication-mail-send" element = {<AuthenticationMailSend />}/> */}
              <Route path="/payment-successfull" element={<PaymentSuccess />} />
              <Route path="/create-customer" element={<CreateCustomer />} />
              <Route path="/choose-package" element={<Billing />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              ></Route>
              <Route path="/display/:projectId" element={<ExternalDisplay />} />
              <Route path="/display/:projectId/v2" element={<ExternalDisplayV2 />} />
              <Route path="/display/:projectId/v3" element={<ExternalDisplayV3 />} />
              <Route
                path="/tree-profile/:id"
                element={
                  <PublicRoute>
                    <TreeProfileDetail />
                  </PublicRoute>
                }
              />
              <Route
                path="/public/project/:pid/map"
                element={
                  <PublicRoute>
                    <StandaloneMapView isPublic={true} />
                  </PublicRoute>
                }
              />
              <Route
                path="/trees"
                element={
                  <ProtectedRoute>
                    <PaidOnlyRoute>
                      <PermissionRoute permission="view_tree">
                        <TreePage />
                      </PermissionRoute>
                    </PaidOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/customers"
                element={
                  <ProtectedRoute>
                    <PaidOnlyRoute>
                      <PermissionRoute permission="view_users">
                        <AdminCustomerPage />
                      </PermissionRoute>
                    </PaidOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers"
                element={
                  <ProtectedRoute>
                    <PaidOnlyRoute>
                      <PermissionRoute permission="view_company_info">
                        <CustomerPage />
                      </PermissionRoute>
                    </PaidOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sensors"
                element={
                  <ProtectedRoute>
                    <SensorPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tasks"
                element={
                  <ProtectedRoute>
                    <PaidOnlyRoute>
                      <PermissionRoute permission="view_tasks">
                        <TaskPage />
                      </PermissionRoute>
                    </PaidOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/task/:id"
                element={
                  <ProtectedRoute>
                    <PaidOnlyRoute>
                      <PermissionRoute permission="view_tasks">
                        <TaskPage />
                      </PermissionRoute>
                    </PaidOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/data-science"
                element={
                  <ProtectedRoute>
                    <PaidOnlyRoute>
                      <PermissionRoute permission="view_data_analysis">
                        <StandaloneDataScience />
                      </PermissionRoute>
                    </PaidOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/map"
                element={
                  <ProtectedRoute>
                    <PaidOnlyRoute>
                      <PermissionRoute permission="view_tree">
                        <StandaloneMapView />
                      </PermissionRoute>
                    </PaidOnlyRoute>
                  </ProtectedRoute>
                }
              />
              {hasPermission(auth?.user, "view_users") && (
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute>
                      <PaidOnlyRoute>
                        <PermissionRoute permission="view_users">
                          <UserPage />
                        </PermissionRoute>
                      </PaidOnlyRoute>
                    </ProtectedRoute>
                  }
                />
              )}
              {hasPermission(auth?.user, "view_users") && (
                <Route
                  path="/user/:id"
                  element={
                    <ProtectedRoute>
                      <PaidOnlyRoute>
                        <PermissionRoute permission="view_users">
                          <UserPage />
                        </PermissionRoute>
                      </PaidOnlyRoute>
                    </ProtectedRoute>
                  }
                />
              )}
              <Route
                path="/tree/:tid"
                element={
                  <ProtectedRoute>
                    <PaidOnlyRoute>
                      <PermissionRoute permission="view_tree">
                        <TreeDetail />
                      </PermissionRoute>
                    </PaidOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sensor/:id"
                element={
                  <ProtectedRoute>
                    <SensorDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customer/:id"
                element={
                  <ProtectedRoute>
                    <PaidOnlyRoute>
                      <PermissionRoute permission="view_company_info">
                        <CustomerPage />
                      </PermissionRoute>
                    </PaidOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/areas"
                element={
                  <ProtectedRoute>
                    <PaidOnlyRoute>
                      <IrrigationAreaPage />
                    </PaidOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                end
                path="/projects"
                element={
                  <ProtectedRoute>
                    <PaidOnlyRoute>
                      <ProjectGridView setProject={setProject} />
                    </PaidOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                end
                path="/project/:pid/list/:listType?"
                element={
                  <ProtectedRoute>
                    <PaidOnlyRoute>
                      <ProjectDetailListView project={project} setProject={setProject} />
                    </PaidOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                end
                path="/project/:pid/dashboard"
                element={
                  <ProtectedRoute>
                    <PaidOnlyRoute>
                      <ProjectDetailDashboardView project={project} setProject={setProject} />
                    </PaidOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route // mobile view
                path="/project/:pid/list/tree/:tid"
                element={
                  <ProtectedRoute>
                    <PaidOnlyRoute>
                      <TreeDetail />
                    </PaidOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                end
                path="/project/without-project/list/:listType?"
                element={
                  <ProtectedRoute>
                    <PaidOnlyRoute>
                      <WithoutProjectView />
                    </PaidOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                end
                path="/project/without-project/map"
                element={
                  <ProtectedRoute>
                    <PaidOnlyRoute>
                      <WithoutProjectMapView />
                    </PaidOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                end
                path="/project/without-project/dashboard"
                element={
                  <ProtectedRoute>
                    <PaidOnlyRoute>
                      <WithoutProjectDashboardView />
                    </PaidOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/project/without-project/datascience"
                element={
                  <ProtectedRoute>
                    <PaidOnlyRoute>
                      <PermissionRoute permission="view_data_analysis">
                        <DataScience project={null} setProject={noop} />
                      </PermissionRoute>
                    </PaidOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/project/:pid/map"
                element={
                  <ProtectedRoute>
                    <PaidOnlyRoute>
                      <ProjectDetailMapView project={project} setProject={setProject} />
                    </PaidOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/project/:pid/datascience"
                element={
                  <ProtectedRoute>
                    <PaidOnlyRoute>
                      <PermissionRoute permission="view_data_analysis">
                        <DataScience project={project} setProject={setProject} />
                      </PermissionRoute>
                    </PaidOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <SettingsRoute>
                      <SettingsIndexRoute />
                    </SettingsRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/:section"
                element={
                  <ProtectedRoute>
                    <SettingsRoute>
                      <SettingPage />
                    </SettingsRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <PaidOnlyRoute>
                      <ProfilePage />
                    </PaidOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/feedback"
                element={
                  <ProtectedRoute>
                    <FeedbackPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/" element={<Navigate to={getPersonaHomePath(auth?.user)} replace />} />
              <Route path="*" element={<PageNotFound />} />
            </Routes>
          </Suspense>
        </TourWrapper>
        <ReactQueryDevtools />
        <CookieConsentBanner />
      </ProjectFilterProvider>
    </QueryClientProvider>
  );
};
export default App;
